import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout.jsx';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { 
  Video, 
  Plus, 
  Calendar,
  Users,
  Clock,
  PhoneCall,
  MessageSquare,
  CheckCircle,
  Copy,
  ExternalLink
} from 'lucide-react';
import { createConsultation, getConsultations, updateConsultationNotes, getFamilyMembers } from '@/services/api';
import { toast } from 'sonner';

export default function ConsultationsPage() {
  const [consultations, setConsultations] = useState([]);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [selectedConsultation, setSelectedConsultation] = useState(null);
  const [savingNotes, setSavingNotes] = useState(false);
  
  // Form states
  const [title, setTitle] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [consultationNotes, setConsultationNotes] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [consultationsData, membersData] = await Promise.all([
        getConsultations(),
        getFamilyMembers()
      ]);
      setConsultations(consultationsData);
      setFamilyMembers(membersData);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load consultations');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!title.trim() || !scheduledTime) {
      toast.error('Please fill in required fields');
      return;
    }

    setCreating(true);
    try {
      const result = await createConsultation(title, scheduledTime, selectedParticipants);
      toast.success(`Consultation scheduled! Room ID: ${result.room_id}`);
      setDialogOpen(false);
      resetForm();
      await loadData();
    } catch (error) {
      console.error('Create consultation error:', error);
      toast.error('Failed to create consultation');
    } finally {
      setCreating(false);
    }
  };

  const handleSaveNotes = async () => {
    if (!consultationNotes.trim()) {
      toast.error('Please enter consultation notes');
      return;
    }

    setSavingNotes(true);
    try {
      const result = await updateConsultationNotes(selectedConsultation.id, consultationNotes);
      toast.success('Notes saved with AI summary!');
      setNotesDialogOpen(false);
      setConsultationNotes('');
      await loadData();
    } catch (error) {
      console.error('Save notes error:', error);
      toast.error('Failed to save notes');
    } finally {
      setSavingNotes(false);
    }
  };

  const resetForm = () => {
    setTitle('');
    setScheduledTime('');
    setSelectedParticipants([]);
  };

  const openNotesDialog = (consultation) => {
    setSelectedConsultation(consultation);
    setConsultationNotes(consultation.notes || '');
    setNotesDialogOpen(true);
  };

  const copyRoomLink = (roomId) => {
    const link = `${window.location.origin}/consultation/${roomId}`;
    navigator.clipboard.writeText(link);
    toast.success('Room link copied to clipboard!');
  };

  const toggleParticipant = (userId) => {
    setSelectedParticipants(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled': return 'bg-amber-100 text-amber-700';
      case 'completed': return 'bg-green-100 text-green-700';
      case 'cancelled': return 'bg-red-100 text-red-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const scheduledConsultations = consultations.filter(c => c.status === 'scheduled');
  const completedConsultations = consultations.filter(c => c.status === 'completed');

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="consultations-page">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Video Consultations
            </h1>
            <p className="text-slate-600 mt-1">Schedule and manage video consultations with your family and doctor</p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-blue-900 hover:bg-blue-800 text-white rounded-lg px-6 btn-active" data-testid="schedule-consultation-btn">
                <Plus className="w-4 h-4 mr-2" />
                Schedule Consultation
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Schedule Consultation</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Consultation Title *</Label>
                  <Input
                    id="title"
                    placeholder="e.g., Follow-up with Dr. Smith"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    data-testid="consultation-title-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="scheduledTime">Date & Time *</Label>
                  <Input
                    id="scheduledTime"
                    type="datetime-local"
                    value={scheduledTime}
                    onChange={(e) => setScheduledTime(e.target.value)}
                    data-testid="consultation-time-input"
                  />
                </div>

                {familyMembers.length > 0 && (
                  <div className="space-y-2">
                    <Label>Invite Family Members</Label>
                    <div className="space-y-2 max-h-40 overflow-y-auto p-2 bg-slate-50 rounded-lg">
                      {familyMembers.map((member) => (
                        <label
                          key={member.user_id}
                          className="flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-white transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={selectedParticipants.includes(member.user_id)}
                            onChange={() => toggleParticipant(member.user_id)}
                            className="w-4 h-4 text-blue-900 rounded border-slate-300"
                          />
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                              <span className="text-sm font-medium text-blue-900">
                                {member.full_name?.charAt(0)}
                              </span>
                            </div>
                            <div>
                              <p className="text-sm font-medium">{member.full_name}</p>
                              <p className="text-xs text-slate-500">{member.relationship}</p>
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                <Button
                  className="w-full bg-blue-900 hover:bg-blue-800 text-white"
                  onClick={handleCreate}
                  disabled={creating}
                  data-testid="confirm-schedule-btn"
                >
                  {creating ? 'Scheduling...' : 'Schedule Consultation'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Upcoming Consultations */}
        <div>
          <h2 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Upcoming ({scheduledConsultations.length})
          </h2>
          {loading ? (
            <div className="space-y-4">
              {[1, 2].map(i => (
                <div key={i} className="skeleton h-32 rounded-xl" />
              ))}
            </div>
          ) : scheduledConsultations.length === 0 ? (
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardContent className="py-8 text-center">
                <Video className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600">No upcoming consultations</p>
                <p className="text-sm text-slate-400 mt-1">Schedule a video consultation with your doctor</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {scheduledConsultations.map((consultation, index) => (
                <Card 
                  key={consultation.id} 
                  className="consultation-card text-white shadow-lg animate-fade-in"
                  style={{ animationDelay: `${index * 0.1}s`, opacity: 0 }}
                >
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="space-y-2">
                        <h3 className="text-xl font-semibold">{consultation.title}</h3>
                        <div className="flex items-center gap-4 text-blue-100 text-sm">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {new Date(consultation.scheduled_time).toLocaleDateString()}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {new Date(consultation.scheduled_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                          <span className="flex items-center gap-1">
                            <Users className="w-4 h-4" />
                            {(consultation.participants?.length || 0) + 1} participants
                          </span>
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-xs bg-white/20 px-2 py-1 rounded">Room: {consultation.room_id}</span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => copyRoomLink(consultation.room_id)}
                          className="bg-white/20 text-white hover:bg-white/30 border-0"
                        >
                          <Copy className="w-4 h-4 mr-1" />
                          Copy Link
                        </Button>
                        <Button
                          size="sm"
                          className="bg-white text-blue-900 hover:bg-blue-50"
                          onClick={() => openNotesDialog(consultation)}
                        >
                          <MessageSquare className="w-4 h-4 mr-1" />
                          Add Notes
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Completed Consultations */}
        {completedConsultations.length > 0 && (
          <div>
            <h2 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Completed ({completedConsultations.length})
            </h2>
            <div className="space-y-3">
              {completedConsultations.map((consultation, index) => (
                <Card 
                  key={consultation.id} 
                  className="bg-white border-slate-200 shadow-sm card-hover animate-fade-in"
                  style={{ animationDelay: `${index * 0.05}s`, opacity: 0 }}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-4 min-w-0 flex-1">
                        <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <CheckCircle className="w-6 h-6 text-green-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="font-semibold text-slate-900">{consultation.title}</h3>
                          <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3.5 h-3.5" />
                              {new Date(consultation.scheduled_time).toLocaleDateString()}
                            </span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(consultation.status)}`}>
                              Completed
                            </span>
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openNotesDialog(consultation)}
                      >
                        View Notes
                      </Button>
                    </div>
                    {consultation.ai_summary && (
                      <div className="mt-4 p-3 bg-sky-50 rounded-lg border-l-4 border-sky-500">
                        <p className="text-xs font-medium text-sky-700 mb-1">AI Summary</p>
                        <p className="text-sm text-slate-700 line-clamp-2">{consultation.ai_summary}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Notes Dialog */}
        <Dialog open={notesDialogOpen} onOpenChange={setNotesDialogOpen}>
          <DialogContent className="max-w-2xl">
            {selectedConsultation && (
              <>
                <DialogHeader>
                  <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Consultation Notes: {selectedConsultation.title}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Consultation Notes</Label>
                    <Textarea
                      placeholder="Enter consultation notes, symptoms discussed, treatment recommendations..."
                      value={consultationNotes}
                      onChange={(e) => setConsultationNotes(e.target.value)}
                      rows={8}
                      data-testid="consultation-notes-textarea"
                    />
                  </div>

                  {selectedConsultation.ai_summary && (
                    <div className="p-4 bg-green-50 rounded-xl border border-green-200">
                      <h4 className="font-medium text-green-800 mb-2">AI Generated Summary</h4>
                      <p className="text-sm text-slate-700 whitespace-pre-wrap">{selectedConsultation.ai_summary}</p>
                    </div>
                  )}

                  <Button
                    className="w-full bg-blue-900 hover:bg-blue-800 text-white"
                    onClick={handleSaveNotes}
                    disabled={savingNotes}
                    data-testid="save-notes-btn"
                  >
                    {savingNotes ? 'Saving & Generating Summary...' : 'Save Notes & Generate AI Summary'}
                  </Button>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
